import DraftViewer from "components/DraftViewer";
import RejectNotification from "components/Modal/RejectNotification";
import { Image } from "antd";
import dayjs from "dayjs";
import { BackArrow, DeleteIcon } from "themes/Icon";
import ButtonComp from "themes/elements/Button";
import CheckboxComp from "themes/elements/Checkbox";
import DatePickerComp from "themes/elements/DatePicker";
import TimePickerComp from "themes/elements/TimePicker";
import TagComp from "themes/elements/Tag";
import { isPermit } from "utils/permissionCheck";
import style from "../Details/details.module.scss";

const STATUS_LABELS = {
  0: "Draft",
  1: "Pending",
  2: "Approved",
  3: "Rejected",
  4: "Published",
  5: "Scheduled",
  6: "Expired",
};

function inferNotificationType(raw) {
  if (raw?.notification_type != null && raw?.notification_type !== "") {
    const t = Number(raw.notification_type);
    if (t === 1 || t === 2) return t;
  }
  if (raw?.category === "Alert") return 2;
  if (raw?.category === "General") return 1;
  return null;
}

function normalizeNotification(raw) {
  if (!raw) return null;
  const rf = raw.recipients_flg;
  return {
    notification_content_id: raw.notification_content_id ?? raw.id,
    notification_title: raw.notification_title ?? raw.title,
    notification_content: raw.notification_content ?? raw.content,
    notification_type: inferNotificationType(raw),
    content_status_flg: raw.content_status_flg ?? raw.status_flg,
    recipients_flg:
      rf !== undefined && rf !== null
        ? Number(rf)
        : Array.isArray(raw.wards) && raw.wards.length > 0
          ? 2
          : 1,
    wards: Array.isArray(raw.wards) ? raw.wards : [],
    files: Array.isArray(raw.files) ? raw.files : [],
    approval_remarks: raw.approval_remarks ?? raw.remarks,
    alert_date_time: raw.alert_date_time,
    alert_date: raw.alert_date,
    expiry_date: raw.expiry_date,
    expiry_date_time: raw.expiry_date_time,
    start_date_time: raw.start_date_time,
    end_date_time: raw.end_date_time,
    scheduled_flg: raw.scheduled_flg,
    redirection_type: raw.redirection_type,
    redirection_link: raw.redirection_link ?? raw.external_link,
    link_label: raw.link_label ?? raw.display_us,
    created_user_name: raw.created_user_name,
  };
}

function recipientChips({ recipients_flg, wards }) {
  if (recipients_flg === 1) return ["All citizens"];
  if (Array.isArray(wards) && wards.length > 0) {
    return wards.map((w) =>
      [w?.ward_code, w?.region_name || w?.ward_name].filter(Boolean).join(" — ")
    );
  }
  if (recipients_flg === 2) return ["—"];
  return ["All citizens"];
}

function fileIsLikelyImage(f) {
  if (!f) return false;
  if (f.notification_file_mimetype?.startsWith("image/")) return true;
  const name = f.notification_file_name || "";
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(name);
}

function draftHasInlineImages(content) {
  if (!content || typeof content !== "string") return false;
  try {
    const raw = JSON.parse(content);
    const map = raw.entityMap;
    if (!map || typeof map !== "object") return false;
    for (const key of Object.keys(map)) {
      const ent = map[key];
      const type = (ent?.type || "").toUpperCase();
      if (type === "IMAGE") {
        const src = ent?.data?.src;
        if (src != null && String(src).trim() !== "") return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

/** True when there are attachment images or embedded draft images (keep split layout). */
function notificationHasVisualMedia(n) {
  if (!n) return false;
  const files = Array.isArray(n.files) ? n.files : [];
  if (files.some(fileIsLikelyImage)) return true;
  return draftHasInlineImages(n.notification_content);
}

function getRejectionReason(raw) {
  if (!raw) return null;
  const st = raw.content_status_flg ?? raw.status_flg;
  if (Number(st) !== 3) return null;
  const activities = raw?.activity || raw?.activities;
  if (Array.isArray(activities)) {
    const rejectEntry = activities.find(
      (a) => a?.news_status_remarks && a?.news_status_remarks !== ""
    );
    if (rejectEntry) return rejectEntry.news_status_remarks;
  }
  return (
    raw?.approval_remarks ??
    raw?.remarks ??
    raw?.rejection_reason ??
    null
  );
}

function redirectionSummary(redirection_type, redirection_link, link_label) {
  if (redirection_type == null) return null;
  if (redirection_type === 0 || redirection_type === "0") return "None";
  if (redirection_type === 2 || redirection_type === "2") {
    return link_label || redirection_link || "External";
  }
  return link_label || redirection_link || "In-app";
}

function getFileIcon(type) {
  if (!type) return null;
  if (type.includes("pdf"))
    return (
      <img
        src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
        alt="pdf"
        width={32}
        height={32}
      />
    );
  if (type.includes("mp4") || type.includes("video"))
    return (
      <img
        src="https://cdn-icons-png.flaticon.com/512/1161/1161738.png"
        alt="video"
        width={32}
        height={32}
      />
    );
  if (type.includes("image"))
    return (
      <img
        src="https://cdn-icons-png.flaticon.com/512/337/337948.png"
        alt="image"
        width={32}
        height={32}
      />
    );
  return null;
}

function handleDownloadFile(file) {
  const link = document.createElement("a");
  link.href = file?.notification_s3_path;
  link.download = file?.notification_file_name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function AttachmentsGallery({ files }) {
  const imageFiles = files.filter((f) =>
    f?.notification_file_mimetype?.startsWith("image/")
  );
  const otherFiles = files.filter(
    (f) => !f?.notification_file_mimetype?.startsWith("image/")
  );

  if (imageFiles.length === 0 && otherFiles.length === 0) return null;

  return (
    <div>
      <label className="input-label">Attachments</label>
      <div className="mt-2">
        {imageFiles.length > 0 && (
          <Image.PreviewGroup>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: otherFiles.length > 0 ? 16 : 0,
              }}
            >
              {imageFiles.map((file, index) => (
                <Image
                  key={file?.notification_media_id || index}
                  src={file?.notification_s3_path}
                  alt={file?.notification_file_name}
                  width={imageFiles.length === 1 ? "100%" : 140}
                  height={imageFiles.length === 1 ? "auto" : 140}
                  style={{
                    objectFit: "cover",
                    borderRadius: 10,
                    maxHeight: imageFiles.length === 1 ? 320 : 140,
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk2tG8fAAAAABJRU5ErkJggg=="
                />
              ))}
            </div>
          </Image.PreviewGroup>
        )}

        {otherFiles.map((file, index) => (
          <div
            key={file?.notification_media_id || index}
            style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {getFileIcon(file?.notification_file_mimetype)}
              <div>
                <div style={{ fontWeight: 500 }}>{file?.notification_file_name}</div>
                {file?.notification_file_size > 0 && (
                  <div style={{ fontSize: 12, color: "#555" }}>
                    {file.notification_file_size_formatted || `${(file.notification_file_size / 1024).toFixed(1)} KB`}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              style={{
                border: "none",
                background: "none",
                textDecoration: "underline",
                cursor: "pointer",
                color: "var(--Dark, #121212)",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "130%",
              }}
              onClick={() => handleDownloadFile(file)}
            >
              Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotificationDetailView({
  notification: notificationProp,
  onApprove,
  onReject,
  onCancel,
  viewStatus,
  onEdit,
  onDelete,
  isLoading = false,
  rejectOpen,
  setRejectOpen,
  rejectFormData,
  setRejectFormData,
  handleReject,
  approveSchedule,
  setApproveSchedule,
  approveScheduleDate,
  setApproveScheduleDate,
  approveScheduleTime,
  setApproveScheduleTime,
}) {
  const n = normalizeNotification(notificationProp);
  if (!n) return null;

  const hasVisualMedia = notificationHasVisualMedia(n);

  const rejectionReason = getRejectionReason(notificationProp);

  const typeLabel =
    n.notification_type === 2 ? "Alert" : n.notification_type === 1 ? "General" : "—";
  const tagColor = n.notification_type === 2 ? "orange" : "blue";

  const renderHeaderActions = () => {
    if (viewStatus === 1) {
      const isScheduled = approveSchedule && approveScheduleDate && approveScheduleTime;
      return (
        <>
          <ButtonComp variant="secondary" onClick={onCancel}>
            Cancel
          </ButtonComp>
          <ButtonComp variant="secondary" onClick={onReject}>
            Reject
          </ButtonComp>
          <ButtonComp onClick={onApprove} loading={isLoading}>
            {isScheduled ? "Approve & Schedule" : "Approve"}
          </ButtonComp>
        </>
      );
    }
    if (viewStatus === 5) {
      return (
        <>
          <ButtonComp variant="secondary" onClick={onCancel}>
            Cancel
          </ButtonComp>
          {typeof onEdit === "function" && (
            <ButtonComp variant="secondary" onClick={() => onEdit()}>
              Edit
            </ButtonComp>
          )}
          {typeof onDelete === "function" && isPermit(4, "delete") && (
            <ButtonComp
              variant="secondary"
              onClick={() => onDelete()}
              prefix={<DeleteIcon />}
            >
              Delete
            </ButtonComp>
          )}
        </>
      );
    }
    if (viewStatus === 4 || viewStatus === 3) {
      return (
        <ButtonComp variant="secondary" onClick={onCancel}>
          Cancel
        </ButtonComp>
      );
    }
    return (
      <ButtonComp variant="secondary" onClick={onCancel}>
        Cancel
      </ButtonComp>
    );
  };

  const statusLabel =
    STATUS_LABELS[n.content_status_flg] ?? `Status ${n.content_status_flg ?? "—"}`;
  const recipients = recipientChips({
    recipients_flg: n.recipients_flg,
    wards: n.wards,
  });

  const basicRows = [
    { key: "Status", value: statusLabel, chips: false },
    { key: "Type", value: typeLabel, chips: false },
    {
      key: "Recipients",
      value: recipients,
      chips: true,
    },
    {
      key: "Scheduled",
      value:
        n.scheduled_flg === 1 || n.scheduled_flg === "1" ? "Yes" : "No",
      chips: false,
    },
    { key: "Alert / send date", value: n.alert_date_time || n.alert_date, chips: false },
    { key: "Expiry date & time", value: n.expiry_date_time || n.expiry_date, chips: false },
    {
      key: "Alert window",
      value:
        n.notification_type === 2
          ? [n.start_date_time, n.end_date_time].filter(Boolean).join(" → ") || null
          : null,
      chips: false,
    },
    {
      key: "Destination",
      value: redirectionSummary(
        n.redirection_type,
        n.redirection_link,
        n.link_label
      ),
      chips: false,
    },
    { key: "Link / screen", value: n.redirection_link, chips: false },
    { key: "Link label", value: n.link_label, chips: false },
    { key: "Created by", value: n.created_user_name, chips: false },
  ];

  return (
    <>
    <div className="w-full flex flex-column">
      <div
        className={`flex gap-2 align-center w-full justify-between ${style["header-wrapper"]}`}
      >
        <div className={`flex gap-2 align-center w-full ${style["title-container"]}`}>
          <span onClick={onCancel} className={`${style["back-icon"]} pointer`}>
            <BackArrow />
          </span>
          <div
            className={`flex wrap align-center gap-4 w-full ${style["title-chip-wrapper"]}`}
          >
            <h2 className="page-title-details">{n.notification_title}</h2>
            <TagComp color={tagColor}>{typeLabel}</TagComp>
          </div>
        </div>
        <div className={`flex align-center gap-4 ${style["button-wrapper"]}`}>
          {renderHeaderActions()}
        </div>
      </div>

      <div
        className={`${style["body-wrapper"]} ${
          !hasVisualMedia ? style["body-wrapper--text-only"] : ""
        }`}
      >
        <div
          className={`${style["left-section"]} ${
            !hasVisualMedia ? style["left-section--text-only"] : ""
          }`}
        >
          {(viewStatus === 3 || n.content_status_flg === 3) &&
          rejectionReason ? (
            <div
              style={{
                background: "#FFF1F0",
                border: "1px solid #FFA39E",
                borderRadius: 8,
                padding: "12px 16px",
              }}
            >
              <p
                style={{
                  fontWeight: 600,
                  color: "#CF1322",
                  fontSize: 14,
                  marginBottom: 4,
                }}
              >
                Reason for rejection
              </p>
              <p style={{ color: "#434343", fontSize: 14, margin: 0 }}>
                {rejectionReason}
              </p>
            </div>
          ) : null}

          <h3 className={`abstract-title ${style["abstract-title"]}`}>
            {n.notification_title}
          </h3>

          <div className={`${style["news-details"]} flex align-center gap-4`}>
            <div className={`flex align-center gap-2 ${style["details-left"]}`}>
              {n.created_user_name ? (
                <h6 className={style["details-text"]}>{n.created_user_name}</h6>
              ) : null}
              {n.created_user_name && n.alert_date_time ? (
                <span className={style["details-dot"]} />
              ) : null}
              {n.alert_date_time ? (
                <h6 className={style["details-text"]}>{n.alert_date_time}</h6>
              ) : null}
            </div>
          </div>

          <div className={`flex flex-column gap-5 ${style["draftjs"]}`}>
            <DraftViewer content={n.notification_content} />
          </div>

          <AttachmentsGallery files={n.files} />
        </div>

        <div
          className={`${style["right-section"]} ${
            !hasVisualMedia ? style["right-section--text-only"] : ""
          }`}
        >
          <div className={style["basic-info-wrapper"]}>
            <h4 className={style["card-title"]}>Basic information:</h4>
            {basicRows.map(
              (row) =>
                row.value != null &&
                row.value !== "" &&
                !(Array.isArray(row.value) && row.value.every((x) => !x)) && (
                  <div key={row.key} className={style["detail-container"]}>
                    <h5 className={style["detail-key"]}>{row.key}:</h5>
                    {row.chips ? (
                      <div className={style["detail-key-list"]}>
                        {row.value.map((item, i) => (
                          <TagComp key={i}>{item}</TagComp>
                        ))}
                      </div>
                    ) : (
                      <h6 className={style["detail-value"]}>{row.value}</h6>
                    )}
                  </div>
                )
            )}
          </div>
          {viewStatus === 1 && typeof setApproveSchedule === "function" && (
            <div className={style["basic-info-wrapper"]} style={{ marginTop: 16 }}>
              <h4 className={style["card-title"]}>Schedule publishing:</h4>
              <div className="flex flex-column gap-2 mt-2">
                <CheckboxComp
                  isChecked={approveSchedule}
                  setChecked={(val) => {
                    setApproveSchedule(val);
                    if (!val) {
                      setApproveScheduleDate(null);
                      setApproveScheduleTime(null);
                    }
                  }}
                  label="Schedule for publishing"
                />
                {approveSchedule && (
                  <div className="flex align-center gap-4">
                    <DatePickerComp
                      defaultValue={approveScheduleDate}
                      onChange={(dateString) => {
                        setApproveScheduleDate(dateString);
                        setApproveScheduleTime(null);
                      }}
                      disabledDate={(current) => current && current < dayjs().startOf("day")}
                      placeholder="Publish date"
                    />
                    <TimePickerComp
                      date={approveScheduleDate}
                      defaultValue={approveScheduleTime}
                      onChange={(time, time12HourFormat, time24HourFormat) =>
                        setApproveScheduleTime(time24HourFormat)
                      }
                      placeholder="Publish time"
                      disabled={!approveScheduleDate}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    {viewStatus === 1 &&
      typeof handleReject === "function" &&
      setRejectOpen &&
      rejectFormData &&
      setRejectFormData && (
        <RejectNotification
          open={rejectOpen}
          setOpen={setRejectOpen}
          isLoading={isLoading}
          formData={rejectFormData}
          setFormData={setRejectFormData}
          onSubmit={() => {
            setRejectOpen(false);
            handleReject();
          }}
        />
      )}
    </>
  );
}

export default NotificationDetailView;
